import { Link } from 'react-router-dom';
import { Navbar } from '@/components/layout/Navbar';

export function PrivacyPolicyPage() {
    return (
        <div className="bg-background-light dark:bg-background-dark text-slate-900 dark:text-slate-100 font-sans transition-colors duration-300 min-h-screen">
            <Navbar variant="landing" />

            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                {/* Header */}
                <div className="mb-12 text-center">
                    <h1 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-4 font-display">
                        Privacy Policy
                    </h1>
                    <p className="text-slate-600 dark:text-slate-400 text-lg">
                        Last Updated: January 10, 2026
                    </p>
                </div>

                {/* Content */}
                <div className="prose prose-slate dark:prose-invert max-w-none">
                    <div className="bg-surface-light dark:bg-surface-dark rounded-2xl p-8 shadow-sm border border-slate-200 dark:border-slate-800 mb-8">
                        <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">1. Introduction</h2>
                        <p className="text-slate-700 dark:text-slate-300 mb-4">
                            Welcome to UniScout. We are committed to protecting your personal information and your right to privacy.
                            This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use
                            our platform at uniscout.ai ("Services").
                        </p>
                        <p className="text-slate-700 dark:text-slate-300">
                            By using our Services, you agree to the collection and use of information in accordance with this policy.
                            If you do not agree with our policies and practices, please do not use our Services.
                        </p>
                    </div>

                    <div className="bg-surface-light dark:bg-surface-dark rounded-2xl p-8 shadow-sm border border-slate-200 dark:border-slate-800 mb-8">
                        <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">2. Information We Collect</h2>

                        <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3 mt-6">2.1 Personal Information</h3>
                        <p className="text-slate-700 dark:text-slate-300 mb-4">
                            We collect personal information that you provide to us when you register for an account, including:
                        </p>
                        <ul className="list-disc list-inside text-slate-700 dark:text-slate-300 space-y-2 mb-4">
                            <li>Full name and email address</li>
                            <li>Professional title and organization name</li>
                            <li>LinkedIn profile URL</li>
                            <li>Profile photo and organization logo</li>
                            <li>Investment preferences (for investors)</li>
                            <li>Company information and documentation (for startups)</li>
                        </ul>

                        <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3 mt-6">2.2 Documents and Files</h3>
                        <p className="text-slate-700 dark:text-slate-300 mb-4">
                            When you use our Services, you may upload:
                        </p>
                        <ul className="list-disc list-inside text-slate-700 dark:text-slate-300 space-y-2 mb-4">
                            <li>Pitch decks and business plans</li>
                            <li>Financial documents and projections</li>
                            <li>Videos and presentation materials</li>
                            <li>Team information and backgrounds</li>
                        </ul>

                        <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3 mt-6">2.3 Automatically Collected Information</h3>
                        <p className="text-slate-700 dark:text-slate-300 mb-4">
                            We automatically collect certain information when you access our Services:
                        </p>
                        <ul className="list-disc list-inside text-slate-700 dark:text-slate-300 space-y-2">
                            <li>IP address and browser type</li>
                            <li>Device information and operating system</li>
                            <li>Usage data and analytics</li>
                            <li>Cookies and similar tracking technologies</li>
                        </ul>
                    </div>

                    <div className="bg-surface-light dark:bg-surface-dark rounded-2xl p-8 shadow-sm border border-slate-200 dark:border-slate-800 mb-8">
                        <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">3. How We Use Your Information</h2>
                        <p className="text-slate-700 dark:text-slate-300 mb-4">
                            We use the information we collect to:
                        </p>
                        <ul className="list-disc list-inside text-slate-700 dark:text-slate-300 space-y-2">
                            <li>Provide, operate, and maintain our Services</li>
                            <li>Process your registration and manage your account</li>
                            <li>Analyze startup materials using AI to generate investment reports</li>
                            <li>Facilitate connections between investors and startups</li>
                            <li>Send you technical notices and support messages</li>
                            <li>Respond to your inquiries and provide customer support</li>
                            <li>Improve and personalize our Services</li>
                            <li>Monitor and analyze usage trends and activities</li>
                            <li>Detect and prevent fraud or unauthorized access</li>
                            <li>Comply with legal obligations</li>
                        </ul>
                    </div>

                    <div className="bg-surface-light dark:bg-surface-dark rounded-2xl p-8 shadow-sm border border-slate-200 dark:border-slate-800 mb-8">
                        <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">4. AI Processing and Analysis</h2>
                        <p className="text-slate-700 dark:text-slate-300 mb-4">
                            UniScout uses artificial intelligence to analyze startup information. When you upload documents or
                            provide information about your startup, we use AI models (including Google's Gemini) to:
                        </p>
                        <ul className="list-disc list-inside text-slate-700 dark:text-slate-300 space-y-2 mb-4">
                            <li>Extract key information from documents</li>
                            <li>Generate investment analysis and reports</li>
                            <li>Perform market research and competitive analysis</li>
                            <li>Identify trends and insights</li>
                        </ul>
                        <p className="text-slate-700 dark:text-slate-300">
                            All AI processing is performed with appropriate safeguards to protect your confidential information.
                        </p>
                    </div>

                    <div className="bg-surface-light dark:bg-surface-dark rounded-2xl p-8 shadow-sm border border-slate-200 dark:border-slate-800 mb-8">
                        <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">5. Information Sharing and Disclosure</h2>

                        <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3 mt-6">5.1 With Your Consent</h3>
                        <p className="text-slate-700 dark:text-slate-300 mb-4">
                            We share startup information with investors when:
                        </p>
                        <ul className="list-disc list-inside text-slate-700 dark:text-slate-300 space-y-2 mb-4">
                            <li>Startups opt-in to be visible to investors on the platform</li>
                            <li>Investors request analysis of specific startups</li>
                        </ul>

                        <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3 mt-6">5.2 Service Providers</h3>
                        <p className="text-slate-700 dark:text-slate-300 mb-4">
                            We work with third-party service providers who help us operate our platform:
                        </p>
                        <ul className="list-disc list-inside text-slate-700 dark:text-slate-300 space-y-2 mb-4">
                            <li><strong>Google Cloud Platform</strong> - Infrastructure and storage</li>
                            <li><strong>Firebase</strong> - Authentication and database services</li>
                            <li><strong>Google Vertex AI</strong> - AI processing and analysis</li>
                            <li><strong>BigQuery</strong> - Data analytics</li>
                            <li><strong>Resend</strong> - Email delivery</li>
                        </ul>

                        <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3 mt-6">5.3 Legal Requirements</h3>
                        <p className="text-slate-700 dark:text-slate-300">
                            We may disclose your information if required by law or in response to valid requests by public
                            authorities (e.g., court orders or government agencies).
                        </p>
                    </div>

                    <div className="bg-surface-light dark:bg-surface-dark rounded-2xl p-8 shadow-sm border border-slate-200 dark:border-slate-800 mb-8">
                        <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">6. Data Storage and Security</h2>
                        <p className="text-slate-700 dark:text-slate-300 mb-4">
                            We implement appropriate technical and organizational security measures to protect your information:
                        </p>
                        <ul className="list-disc list-inside text-slate-700 dark:text-slate-300 space-y-2 mb-4">
                            <li>Data encryption in transit and at rest</li>
                            <li>Secure authentication using Firebase</li>
                            <li>Regular security assessments and updates</li>
                            <li>Access controls and monitoring</li>
                            <li>Data stored on Google Cloud Platform with enterprise-grade security</li>
                        </ul>
                        <p className="text-slate-700 dark:text-slate-300">
                            However, no method of transmission over the Internet or electronic storage is 100% secure.
                            While we strive to protect your information, we cannot guarantee its absolute security.
                        </p>
                    </div>

                    <div className="bg-surface-light dark:bg-surface-dark rounded-2xl p-8 shadow-sm border border-slate-200 dark:border-slate-800 mb-8">
                        <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">7. Your Rights and Choices</h2>
                        <p className="text-slate-700 dark:text-slate-300 mb-4">
                            You have the following rights regarding your personal information:
                        </p>
                        <ul className="list-disc list-inside text-slate-700 dark:text-slate-300 space-y-2 mb-4">
                            <li><strong>Access</strong> - Request access to your personal information</li>
                            <li><strong>Correction</strong> - Update or correct inaccurate information</li>
                            <li><strong>Deletion</strong> - Request deletion of your account and data</li>
                            <li><strong>Data Portability</strong> - Request a copy of your data in a portable format</li>
                            <li><strong>Opt-out</strong> - Opt-out of certain data collection and marketing communications</li>
                        </ul>
                        <p className="text-slate-700 dark:text-slate-300">
                            To exercise these rights, please contact us at the email address provided below.
                        </p>
                    </div>

                    <div className="bg-surface-light dark:bg-surface-dark rounded-2xl p-8 shadow-sm border border-slate-200 dark:border-slate-800 mb-8">
                        <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">8. Cookies and Tracking Technologies</h2>
                        <p className="text-slate-700 dark:text-slate-300 mb-4">
                            We use cookies and similar tracking technologies to:
                        </p>
                        <ul className="list-disc list-inside text-slate-700 dark:text-slate-300 space-y-2 mb-4">
                            <li>Remember your preferences and settings</li>
                            <li>Maintain your session and authentication</li>
                            <li>Analyze how you use our Services</li>
                            <li>Improve our platform's performance</li>
                        </ul>
                        <p className="text-slate-700 dark:text-slate-300">
                            You can control cookies through your browser settings. However, disabling cookies may affect
                            your ability to use certain features of our Services.
                        </p>
                    </div>

                    <div className="bg-surface-light dark:bg-surface-dark rounded-2xl p-8 shadow-sm border border-slate-200 dark:border-slate-800 mb-8">
                        <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">9. Data Retention</h2>
                        <p className="text-slate-700 dark:text-slate-300">
                            We retain your personal information for as long as necessary to provide our Services and fulfill
                            the purposes outlined in this Privacy Policy. When you delete your account, we will delete or
                            anonymize your personal information, except where we are required to retain it for legal,
                            regulatory, or legitimate business purposes.
                        </p>
                    </div>

                    <div className="bg-surface-light dark:bg-surface-dark rounded-2xl p-8 shadow-sm border border-slate-200 dark:border-slate-800 mb-8">
                        <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">10. Children's Privacy</h2>
                        <p className="text-slate-700 dark:text-slate-300">
                            Our Services are not intended for individuals under the age of 18. We do not knowingly collect
                            personal information from children. If you become aware that a child has provided us with personal
                            information, please contact us, and we will take steps to delete such information.
                        </p>
                    </div>

                    <div className="bg-surface-light dark:bg-surface-dark rounded-2xl p-8 shadow-sm border border-slate-200 dark:border-slate-800 mb-8">
                        <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">11. International Data Transfers</h2>
                        <p className="text-slate-700 dark:text-slate-300">
                            Your information may be transferred to and maintained on servers located outside of your country.
                            We use Google Cloud Platform infrastructure, which may process data in various regions globally.
                            By using our Services, you consent to the transfer of your information to these locations.
                        </p>
                    </div>

                    <div className="bg-surface-light dark:bg-surface-dark rounded-2xl p-8 shadow-sm border border-slate-200 dark:border-slate-800 mb-8">
                        <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">12. Changes to This Privacy Policy</h2>
                        <p className="text-slate-700 dark:text-slate-300">
                            We may update this Privacy Policy from time to time. We will notify you of any material changes
                            by posting the new Privacy Policy on this page and updating the "Last Updated" date. Your continued
                            use of our Services after such changes constitutes your acceptance of the updated Privacy Policy.
                        </p>
                    </div>

                    <div className="bg-surface-light dark:bg-surface-dark rounded-2xl p-8 shadow-sm border border-slate-200 dark:border-slate-800 mb-8">
                        <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">13. Contact Us</h2>
                        <p className="text-slate-700 dark:text-slate-300 mb-4">
                            If you have any questions about this Privacy Policy or our privacy practices, please contact us:
                        </p>
                        <div className="bg-slate-100 dark:bg-slate-800 rounded-lg p-4 mt-4">
                            <p className="text-slate-700 dark:text-slate-300">
                                <strong>Email:</strong> privacy@uniscout.ai
                            </p>
                            <p className="text-slate-700 dark:text-slate-300 mt-2">
                                <strong>Website:</strong> <a href="https://uniscout.ai" className="text-primary hover:underline">uniscout.ai</a>
                            </p>
                        </div>
                    </div>

                    {/* Back to Home Button */}
                    <div className="text-center mt-12">
                        <Link
                            to="/"
                            className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-logo-gradient text-white font-bold shadow-xl shadow-primary/25 hover:shadow-primary/30 transition-all transform hover:-translate-y-0.5"
                        >
                            <span className="material-icons">home</span>
                            Back to Home
                        </Link>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <footer className="bg-background-light dark:bg-background-dark border-t border-slate-200 dark:border-slate-800 pt-12 pb-8 mt-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <img alt="uniscout Logo" className="h-10 mb-4 mx-auto" src="/logo.png" />
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                            © 2026 UniScout. All rights reserved.
                        </p>
                    </div>
                </div>
            </footer>
        </div>
    );
}
